/** Shared demo catalog for Super Admin User & Role Management screens. */

export const SA_ACCESS_MODULES = [
  { id: "marketplace", label: "Marketplace" },
  { id: "rfq", label: "RFQ & Quotes" },
  { id: "orders", label: "Orders" },
  { id: "payments", label: "Payments" },
  { id: "logistics", label: "Logistics" },
  { id: "disputes", label: "Disputes" },
  { id: "cms", label: "CMS Management" },
  { id: "reports", label: "Reports & Analytics" },
];

export const SA_ACCESS_PERMISSIONS = ["view", "create", "edit", "delete"];

/**
 * Platform-aware permission catalogue used by Create Role.
 * Resource keys are stable authorization identifiers; actions are intentionally
 * limited to operations that make sense for each resource.
 */
export const SA_PLATFORM_PERMISSION_GROUPS = [
  {
    id: "access_control",
    label: "Identity & Access",
    description: "Platform users, invitations, roles and access governance",
    resources: [
      { id: "access_control.users", label: "Platform Users", actions: ["list", "view", "invite", "update", "set_status"] },
      { id: "access_control.roles", label: "Roles & Templates", actions: ["list", "view", "create", "update", "delete", "manage_permissions"] },
      { id: "access_control.assignments", label: "Role Assignments", actions: ["view", "assign", "revoke"] },
      { id: "access_control.audit", label: "Access Audit", actions: ["view", "export"] },
    ],
  },
  {
    id: "platform",
    label: "Platform Governance",
    description: "Module availability, global settings and notifications",
    resources: [
      { id: "platform.dashboard", label: "Super Admin Dashboard", actions: ["view"] },
      { id: "platform.modules", label: "Platform Modules", actions: ["view", "configure", "set_status"] },
      { id: "platform.settings", label: "Global Settings", actions: ["view", "configure"] },
      { id: "platform.notifications", label: "Notifications", actions: ["list", "view", "create", "update"] },
    ],
  },
  {
    id: "master_data",
    label: "Master Data",
    description: "Shared catalogues and reference data used across the platform",
    resources: [
      { id: "master_data.product_categories", label: "Product Categories", actions: ["list", "view", "create", "update", "delete", "bulk_import"] },
      { id: "master_data.locations", label: "Countries, States & Cities", actions: ["list", "view", "create", "update", "delete"] },
      { id: "master_data.factories", label: "Factory Masters", actions: ["list", "view", "create", "update", "delete"] },
      { id: "master_data.auction_categories", label: "Auction Categories", actions: ["list", "view", "create", "update", "delete", "bulk_import"] },
      { id: "master_data.cm_industries", label: "CM Industry Types", actions: ["list", "view", "create", "update", "delete", "bulk_import"] },
      { id: "master_data.facility_categories", label: "Facility Categories", actions: ["list", "view", "create", "update", "delete", "bulk_import"] },
      { id: "master_data.products", label: "Product Bulk Upload", actions: ["view", "bulk_import", "export"] },
      { id: "master_data.banners", label: "Banners", actions: ["list", "view", "create", "update", "delete", "reorder"] },
    ],
  },
  {
    id: "content",
    label: "Content & Experience",
    description: "CMS, legal content and public site presentation",
    resources: [
      { id: "content.cms_pages", label: "CMS Pages", actions: ["list", "view", "create", "update", "publish", "unpublish", "view_history"] },
      { id: "content.terms", label: "Terms & Conditions", actions: ["view", "create", "update", "publish", "unpublish", "delete", "view_history"] },
      { id: "content.home_page", label: "Home Page Sections", actions: ["view", "update", "reorder"] },
      { id: "content.quick_links", label: "Service Quick Links", actions: ["list", "create", "update", "delete", "reorder"] },
      { id: "content.auth_page", label: "Authentication Page", actions: ["view", "update"] },
    ],
  },
  {
    id: "marketplace",
    label: "Marketplace",
    description: "Buyer and seller commerce workflows",
    resources: [
      { id: "marketplace.products", label: "Products & Catalogue", actions: ["list", "view", "create", "update", "delete"] },
      { id: "marketplace.rfqs", label: "RFQs", actions: ["list", "view", "create", "update", "cancel"] },
      { id: "marketplace.quotations", label: "Quotations", actions: ["list", "view", "create", "update", "accept", "reject"] },
      { id: "marketplace.purchase_orders", label: "Purchase Orders", actions: ["list", "view", "create", "update", "cancel"] },
      { id: "marketplace.invoices", label: "Invoices", actions: ["list", "view", "create", "approve", "reject"] },
      { id: "marketplace.fulfilment", label: "Shipping, E-way Bills & GRN", actions: ["list", "view", "create", "update", "confirm"] },
    ],
  },
  {
    id: "auction",
    label: "Auctions",
    description: "Auction listings, bidding, award and settlement",
    resources: [
      { id: "auction.listings", label: "Auction Listings", actions: ["list", "view", "create", "update", "publish", "extend", "cancel", "end"] },
      { id: "auction.bids", label: "Bids & Winner Selection", actions: ["list", "view", "accept", "reject"] },
      { id: "auction.payments", label: "Credits, Payments & Invoices", actions: ["view", "create", "approve", "manage_payment"] },
      { id: "auction.inspections", label: "Auction Inspections", actions: ["list", "view", "assign", "approve", "reject"] },
    ],
  },
  {
    id: "contract_manufacturing",
    label: "Contract Manufacturing",
    description: "Manufacturing RFQs, agreements, production and quality",
    resources: [
      { id: "contract_manufacturing.manufacturers", label: "Manufacturer Directory", actions: ["list", "view", "create", "update", "set_status"] },
      { id: "contract_manufacturing.rfqs", label: "CM RFQs & Quotes", actions: ["list", "view", "create", "update", "accept", "reject"] },
      { id: "contract_manufacturing.agreements", label: "Purchase Agreements", actions: ["list", "view", "create", "update", "approve"] },
      { id: "contract_manufacturing.quality", label: "Quality Control", actions: ["list", "view", "approve", "reject"] },
      { id: "contract_manufacturing.production", label: "Production & Fulfilment", actions: ["list", "view", "update", "change_status", "confirm"] },
    ],
  },
  {
    id: "infrastructure",
    label: "Infrastructure Leasing",
    description: "Listings, leases, commercial operations and disputes",
    resources: [
      { id: "infrastructure.listings", label: "Listings & Inventory", actions: ["list", "view", "create", "update", "delete", "publish"] },
      { id: "infrastructure.enquiries", label: "Requirements & Enquiries", actions: ["list", "view", "assign", "update", "close"] },
      { id: "infrastructure.agreements", label: "Agreements & Documents", actions: ["list", "view", "create", "update", "approve", "finalise"] },
      { id: "infrastructure.payments", label: "Payments & Settlements", actions: ["list", "view", "approve", "manage_payment", "export"] },
      { id: "infrastructure.disputes", label: "Disputes", actions: ["list", "view", "assign", "resolve", "close", "adjust"] },
      { id: "infrastructure.reports", label: "Infrastructure Reports", actions: ["view", "export"] },
    ],
  },
  {
    id: "logistics",
    label: "Logistics",
    description: "Provider, RFQ, shipment and delivery operations",
    resources: [
      { id: "logistics.providers", label: "Logistics Providers", actions: ["list", "view", "create", "update", "set_status"] },
      { id: "logistics.rfqs", label: "Logistics RFQs & Quotes", actions: ["list", "view", "create", "update", "accept", "reject"] },
      { id: "logistics.shipments", label: "Pickup, Transit & Delivery", actions: ["list", "view", "assign", "change_status", "confirm"] },
      { id: "logistics.invoices", label: "Provider Invoices", actions: ["list", "view", "create", "approve", "reject"] },
      { id: "logistics.integration", label: "Module Integration", actions: ["view", "configure"] },
      { id: "logistics.reports", label: "Performance Reports", actions: ["view", "export"] },
    ],
  },
  {
    id: "quality_inspection",
    label: "Quality Inspection",
    description: "QA requests, inspector assignments and inspection outcomes",
    resources: [
      { id: "quality_inspection.requests", label: "QA Requests", actions: ["list", "view", "create", "update", "cancel"] },
      { id: "quality_inspection.quotations", label: "Inspector Quotations", actions: ["list", "view", "create", "accept", "reject"] },
      { id: "quality_inspection.assignments", label: "Inspector Assignments", actions: ["list", "view", "assign", "reassign"] },
      { id: "quality_inspection.inspections", label: "Inspections & Reports", actions: ["list", "view", "update", "complete", "approve", "reject"] },
      { id: "quality_inspection.metrics", label: "Quality Metrics", actions: ["view", "export"] },
    ],
  },
  {
    id: "reporting",
    label: "Reporting & Audit",
    description: "Cross-platform reporting, exports and activity history",
    resources: [
      { id: "reporting.dashboards", label: "Cross-platform Dashboards", actions: ["view"] },
      { id: "reporting.reports", label: "Platform Reports", actions: ["list", "view", "export"] },
      { id: "reporting.audit_logs", label: "Audit Logs", actions: ["list", "view", "export"] },
    ],
  },
  {
    id: "support",
    label: "Notifications, Disputes & Support",
    description: "Cross-module cases, exceptions and support operations",
    resources: [
      { id: "support.notifications", label: "Operational Notifications", actions: ["list", "view", "update"] },
      { id: "support.cases", label: "Support Cases", actions: ["list", "view", "assign", "update", "resolve", "close"] },
      { id: "support.disputes", label: "Cross-module Disputes", actions: ["list", "view", "assign", "resolve", "close", "adjust"] },
    ],
  },
];

export const SA_PERMISSION_ACTION_LABELS = {
  list: "List",
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete",
  invite: "Invite",
  assign: "Assign",
  reassign: "Reassign",
  revoke: "Revoke",
  approve: "Approve",
  reject: "Reject",
  accept: "Accept",
  confirm: "Confirm",
  complete: "Complete",
  publish: "Publish",
  unpublish: "Unpublish",
  cancel: "Cancel",
  close: "Close",
  resolve: "Resolve",
  adjust: "Adjust",
  end: "End",
  extend: "Extend",
  finalise: "Finalise",
  export: "Export",
  bulk_import: "Bulk Import",
  configure: "Configure",
  reorder: "Reorder",
  set_status: "Set Status",
  change_status: "Change Status",
  manage_permissions: "Manage Permissions",
  manage_members: "Manage Members",
  manage_payment: "Manage Payment",
  manage_pricing: "Manage Pricing",
  view_history: "View History",
};

export const SA_ACCESS_ROLES = [
  {
    id: "buyer-admin",
    name: "Buyer Admin",
    description: "Manage buyers, RFQs, orders and procurement activities",
    usersAssigned: 18,
    type: "Custom",
    status: "Active",
    tone: "blue",
    permissions: {
      marketplace: { view: true, create: true, edit: true, delete: true },
      rfq: { view: true, create: true, edit: true, delete: false },
      orders: { view: true, create: true, edit: true, delete: false },
      payments: { view: true, create: true, edit: false, delete: false },
      logistics: { view: true, create: true, edit: true, delete: false },
      disputes: { view: true, create: true, edit: true, delete: true },
      cms: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
    },
  },
  {
    id: "seller-admin",
    name: "Seller Admin",
    description: "Manage sellers, catalog, quotations and order fulfillment",
    usersAssigned: 25,
    type: "Custom",
    status: "Active",
    tone: "orange",
    permissions: {
      marketplace: { view: true, create: true, edit: true, delete: false },
      rfq: { view: true, create: true, edit: true, delete: false },
      orders: { view: true, create: true, edit: true, delete: false },
      payments: { view: true, create: false, edit: false, delete: false },
      logistics: { view: true, create: true, edit: false, delete: false },
      disputes: { view: true, create: true, edit: false, delete: false },
      cms: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
    },
  },
  {
    id: "logistics-manager",
    name: "Logistics Manager",
    description: "Manage shipments, carriers and delivery operations",
    usersAssigned: 10,
    type: "Custom",
    status: "Active",
    tone: "cyan",
    permissions: {
      marketplace: { view: true, create: false, edit: false, delete: false },
      rfq: { view: true, create: false, edit: false, delete: false },
      orders: { view: true, create: false, edit: false, delete: false },
      payments: { view: false, create: false, edit: false, delete: false },
      logistics: { view: true, create: true, edit: true, delete: false },
      disputes: { view: true, create: true, edit: false, delete: false },
      cms: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
    },
  },
  {
    id: "finance-manager",
    name: "Finance Manager",
    description: "Manage invoices, payments, refunds and financial reports",
    usersAssigned: 8,
    type: "Custom",
    status: "Active",
    tone: "purple",
    permissions: {
      marketplace: { view: true, create: false, edit: false, delete: false },
      rfq: { view: true, create: false, edit: false, delete: false },
      orders: { view: true, create: false, edit: false, delete: false },
      payments: { view: true, create: true, edit: true, delete: false },
      logistics: { view: false, create: false, edit: false, delete: false },
      disputes: { view: true, create: false, edit: false, delete: false },
      cms: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: true, edit: false, delete: false },
    },
  },
  {
    id: "system-admin",
    name: "System Admin",
    description: "Full platform access for system configuration and governance",
    usersAssigned: 2,
    type: "System",
    status: "Active",
    tone: "navy",
    permissions: {
      marketplace: { view: true, create: true, edit: true, delete: true },
      rfq: { view: true, create: true, edit: true, delete: true },
      orders: { view: true, create: true, edit: true, delete: true },
      payments: { view: true, create: true, edit: true, delete: true },
      logistics: { view: true, create: true, edit: true, delete: true },
      disputes: { view: true, create: true, edit: true, delete: true },
      cms: { view: true, create: true, edit: true, delete: true },
      reports: { view: true, create: true, edit: true, delete: true },
    },
  },
  {
    id: "procurement-admin",
    name: "Procurement Admin",
    description: "Oversee sourcing, approvals and purchase workflows",
    usersAssigned: 12,
    type: "Custom",
    status: "Active",
    tone: "pink",
    permissions: {
      marketplace: { view: true, create: true, edit: true, delete: false },
      rfq: { view: true, create: true, edit: true, delete: false },
      orders: { view: true, create: true, edit: true, delete: false },
      payments: { view: true, create: false, edit: false, delete: false },
      logistics: { view: true, create: false, edit: false, delete: false },
      disputes: { view: true, create: true, edit: false, delete: false },
      cms: { view: false, create: false, edit: false, delete: false },
      reports: { view: true, create: false, edit: false, delete: false },
    },
  },
];

export const SA_ACCESS_USERS = [
  {
    id: "USR1001",
    firstName: "Arun",
    lastName: "Kumar",
    email: "arun.kumar@abc.com",
    mobile: "9876543210",
    countryCode: "+91",
    company: "ABC Pvt Ltd",
    department: "Procurement",
    roleId: "buyer-admin",
    role: "Buyer Admin",
    modules: ["marketplace", "rfq", "orders"],
    status: "Active",
    lastLogin: "Today, 10:30 AM",
  },
  {
    id: "USR1002",
    firstName: "Priya",
    lastName: "Sharma",
    email: "priya.sharma@global.com",
    mobile: "9988776655",
    countryCode: "+91",
    company: "Global Traders",
    department: "Sales",
    roleId: "seller-admin",
    role: "Seller Admin",
    modules: ["marketplace", "orders", "payments"],
    status: "Active",
    lastLogin: "Yesterday, 4:12 PM",
  },
  {
    id: "USR1003",
    firstName: "Rahul",
    lastName: "Mehta",
    email: "rahul.mehta@fincorp.com",
    mobile: "9123456780",
    countryCode: "+91",
    company: "FinCorp India",
    department: "Finance",
    roleId: "finance-manager",
    role: "Finance User",
    modules: ["payments", "reports"],
    status: "Inactive",
    lastLogin: "2 days ago",
  },
  {
    id: "USR1004",
    firstName: "Sneha",
    lastName: "Patil",
    email: "sneha.patil@procurex.com",
    mobile: "9090909090",
    countryCode: "+91",
    company: "ProcureX",
    department: "Buying",
    roleId: "procurement-admin",
    role: "Procurement Admin",
    modules: ["rfq", "orders", "disputes"],
    status: "Pending",
    lastLogin: "—",
  },
  {
    id: "USR1005",
    firstName: "Vikram",
    lastName: "Singh",
    email: "vikram.singh@shipfast.com",
    mobile: "9811112233",
    countryCode: "+91",
    company: "ShipFast Logistics",
    department: "Operations",
    roleId: "logistics-manager",
    role: "Logistics User",
    modules: ["logistics", "orders"],
    status: "Active",
    lastLogin: "Today, 8:05 AM",
  },
];

export const SA_ACCESS_COMPANIES = [
  "ABC Pvt Ltd",
  "Global Traders",
  "FinCorp India",
  "ProcureX",
  "ShipFast Logistics",
  "Trocanet Internal",
];

export const SA_ACCESS_DEPARTMENTS = [
  "Procurement",
  "Sales",
  "Finance",
  "Buying",
  "Operations",
  "IT",
  "Support",
];

const STORAGE_USERS = "sa_access_users_v1";
const STORAGE_ROLES = "sa_access_roles_v1";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getAccessUsers() {
  return readJson(STORAGE_USERS, SA_ACCESS_USERS);
}

export function saveAccessUsers(users) {
  writeJson(STORAGE_USERS, users);
}

export function getAccessRoles() {
  return readJson(STORAGE_ROLES, SA_ACCESS_ROLES);
}

export function saveAccessRoles(roles) {
  writeJson(STORAGE_ROLES, roles);
}

export function createEmptyPermissionMatrix() {
  return Object.fromEntries(
    SA_PLATFORM_PERMISSION_GROUPS.flatMap((group) =>
      group.resources.map((resource) => [
        resource.id,
        Object.fromEntries(resource.actions.map((action) => [action, false])),
      ]),
    ),
  );
}

export function getRoleTone(role) {
  const found = getAccessRoles().find((item) => item.name === role || item.id === role);
  return found?.tone || "blue";
}
